import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Header, Content } from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import MenuList from '@material-ui/core/MenuList';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import {
  PlatformOverviewGraphView,
  GraphKindFilter,
  NamespaceScopeFilter,
  buildDynamicView,
  getEffectiveKinds,
  APPLICATION_VIEW,
  CLUSTER_NAMESPACE,
  CLUSTER_SCOPED_KINDS,
  useProjects,
  type ProjectEntry,
  type EntityNode,
} from '@openchoreo/backstage-plugin-react';
import { useQueryParams } from '@openchoreo/backstage-plugin';

const useStyles = makeStyles(theme => ({
  content: {
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  triggerButton: {
    textTransform: 'none',
    fontSize: '0.8rem',
    fontWeight: 500,
    padding: theme.spacing(0.5, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: theme.palette.text.secondary,
    },
  },
  popoverPaper: {
    minWidth: 220,
  },
  menuItem: {
    minHeight: 36,
    paddingTop: 2,
    paddingBottom: 2,
  },
  checkbox: {
    padding: 4,
  },
  subheader: {
    lineHeight: '28px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
  },
}));

const DEFAULT_KINDS = APPLICATION_VIEW.kinds;
const DEFAULT_SCOPE = CLUSTER_NAMESPACE;

function useNamespaces() {
  const catalogApi = useApi(catalogApiRef);
  const [namespaces, setNamespaces] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNamespaces = useCallback(async () => {
    try {
      const response = await catalogApi.getEntities({
        filter: { kind: 'Domain' },
        fields: ['metadata.name'],
      });
      const names = response.items.map(e => e.metadata.name).sort();
      if (names.length > 0) {
        setNamespaces(names);
      }
    } catch {
      // Namespace fetch is non-critical; fall back to empty
    } finally {
      setLoading(false);
    }
  }, [catalogApi]);

  useEffect(() => {
    fetchNamespaces();
  }, [fetchNamespaces]);

  return { namespaces, loading };
}

/** Unique key for a project entry, used for query param encoding. */
function projectKey(p: ProjectEntry): string {
  return `${p.namespace}/${p.name}`;
}

export function PlatformOverviewPage() {
  const classes = useStyles();
  const [params, setParams] = useQueryParams<{
    kinds: string;
    scope: string;
    excludedProjects: string | undefined;
  }>({
    kinds: { defaultValue: DEFAULT_KINDS.join(',') },
    scope: { defaultValue: DEFAULT_SCOPE },
    excludedProjects: { defaultValue: undefined },
  });
  const navigate = useNavigate();
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const { namespaces: availableNamespaces, loading: namespacesLoading } =
    useNamespaces();

  // --- Scope state ---
  const selectedScopes = useMemo(
    () =>
      typeof params.scope === 'string'
        ? params.scope.split(',').filter(Boolean)
        : DEFAULT_SCOPE.split(','),
    [params.scope],
  );
  const clusterSelected = selectedScopes.includes(CLUSTER_NAMESPACE);
  const selectedNamespaces = useMemo(
    () => selectedScopes.filter(s => s !== CLUSTER_NAMESPACE),
    [selectedScopes],
  );

  // Once namespaces load, ensure the scope includes a valid namespace.
  // If none of the currently selected namespaces are available, replace with
  // the preferred namespace (prefer "default" if available, else first in list).
  const scopeInitialised = useRef(false);
  useEffect(() => {
    if (namespacesLoading || scopeInitialised.current) return;
    if (availableNamespaces.length === 0) return;
    scopeInitialised.current = true;

    const filteredValid = selectedNamespaces.filter(ns =>
      availableNamespaces.includes(ns),
    );

    if (filteredValid.length > 0) {
      // Some namespaces are valid — only update if stale ones were removed
      if (filteredValid.length === selectedNamespaces.length) return;
      const newScopes = clusterSelected
        ? [CLUSTER_NAMESPACE, ...filteredValid]
        : filteredValid;
      setParams({ scope: newScopes.join(',') }, { replace: true });
      return;
    }

    // No valid namespaces — fall back to preferred
    const preferred = availableNamespaces.includes('default')
      ? 'default'
      : availableNamespaces[0];
    const newScopes = clusterSelected
      ? [CLUSTER_NAMESPACE, preferred]
      : [preferred];
    setParams({ scope: newScopes.join(',') }, { replace: true });
  }, [
    namespacesLoading,
    availableNamespaces,
    selectedNamespaces,
    clusterSelected,
    setParams,
  ]);

  // Namespaces for project fetching (excludes cluster namespace).
  // Pass [] when no namespaces selected (cluster-only) so useProjects returns
  // empty instead of fetching all projects unfiltered.
  const projectNamespaces = useMemo(
    () => (selectedNamespaces.length > 0 ? selectedNamespaces : []),
    [selectedNamespaces],
  );
  const projects = useProjects(projectNamespaces);

  const [projectAnchor, setProjectAnchor] = useState<HTMLButtonElement | null>(
    null,
  );

  // Cluster row should appear in the scope picker regardless of catalog state.
  const scopePickerNamespaces = useMemo(() => {
    const set = new Set<string>(availableNamespaces);
    set.add(CLUSTER_NAMESPACE);
    return [...set];
  }, [availableNamespaces]);

  const handleScopeChange = useCallback(
    (next: string[]) => {
      setParams({ scope: next.join(',') });
    },
    [setParams],
  );

  // --- Kinds ---
  const selectedKinds = useMemo(
    () =>
      typeof params.kinds === 'string'
        ? params.kinds.split(',').filter(Boolean)
        : DEFAULT_KINDS,
    [params.kinds],
  );

  // Build effective view with auto-included cluster kinds
  const effectiveView = useMemo(() => {
    const effectiveKinds = getEffectiveKinds(selectedKinds, clusterSelected);
    return buildDynamicView(effectiveKinds);
  }, [selectedKinds, clusterSelected]);

  // The user-facing view (for description/subtitle) uses the raw selected kinds
  const displayView = useMemo(
    () => buildDynamicView(selectedKinds),
    [selectedKinds],
  );

  // --- Clear excluded projects when scope changes or system kind is deselected ---
  // --- Also strip cluster kinds from URL when cluster scope is toggled off ---
  const currentScope = params.scope;
  const systemKindSelected = selectedKinds.includes('system');
  const prevScopeRef = useRef(currentScope);
  const prevSystemKindRef = useRef(systemKindSelected);
  const prevClusterRef = useRef(clusterSelected);
  useEffect(() => {
    const scopeChanged = currentScope !== prevScopeRef.current;
    const systemDeselected = prevSystemKindRef.current && !systemKindSelected;
    const clusterTurnedOff = prevClusterRef.current && !clusterSelected;
    const clusterTurnedOn = !prevClusterRef.current && clusterSelected;
    prevScopeRef.current = currentScope;
    prevSystemKindRef.current = systemKindSelected;
    prevClusterRef.current = clusterSelected;

    if ((scopeChanged || systemDeselected) && params.excludedProjects) {
      setParams({ excludedProjects: undefined }, { replace: true });
    }

    // When cluster scope is turned off, remove cluster-scoped kinds from the URL
    if (clusterTurnedOff) {
      const cleaned = selectedKinds.filter(
        k => !CLUSTER_SCOPED_KINDS.includes(k),
      );
      if (cleaned.length > 0 && cleaned.length < selectedKinds.length) {
        setParams({ kinds: cleaned.join(',') }, { replace: true });
      }
    }

    // When cluster scope is turned on, re-add cluster-scoped kinds to the URL
    if (clusterTurnedOn) {
      const currentSet = new Set(selectedKinds);
      const missing = CLUSTER_SCOPED_KINDS.filter(k => !currentSet.has(k));
      if (missing.length > 0) {
        setParams(
          { kinds: [...selectedKinds, ...missing].join(',') },
          { replace: true },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScope, systemKindSelected, clusterSelected]);

  // --- Project filter ---
  const excludedSet = useMemo(
    () =>
      new Set(
        params.excludedProjects
          ? params.excludedProjects.split(',').filter(Boolean)
          : [],
      ),
    [params.excludedProjects],
  );

  const selectedProjects = useMemo(
    () => projects.filter(p => !excludedSet.has(projectKey(p))),
    [projects, excludedSet],
  );

  const handleProjectToggle = useCallback(
    (project: ProjectEntry) => {
      const key = projectKey(project);
      const next = new Set(excludedSet);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      setParams({
        excludedProjects: next.size > 0 ? [...next].join(',') : undefined,
      });
    },
    [excludedSet, setParams],
  );

  const showProjectFilter =
    selectedKinds.includes('system') && projects.length > 0;

  // --- Kind filter ---
  const handleKindsChange = useCallback(
    (kinds: string[]) => {
      setParams({ kinds: kinds.join(',') });
    },
    [setParams],
  );

  // --- Node click ---
  const handleNodeClick = useCallback(
    (node: EntityNode, _event: MouseEvent<unknown>) => {
      const route = catalogEntityRoute({
        kind: node.kind ?? 'component',
        namespace: node.namespace ?? 'default',
        name: node.name,
      });
      navigate(route);
    },
    [catalogEntityRoute, navigate],
  );

  // --- Project filter labels ---
  const multiNamespace = selectedNamespaces.length > 1;
  const projectButtonLabel = useMemo(() => {
    if (selectedProjects.length === projects.length) return 'Project: All';
    return `Project: ${selectedProjects.length} of ${projects.length}`;
  }, [selectedProjects.length, projects.length]);

  const projectDisplayName = useCallback(
    (p: ProjectEntry) => (multiNamespace ? `${p.namespace}/${p.name}` : p.name),
    [multiNamespace],
  );

  // --- Scope selector ---
  const scopeLeading = (
    <NamespaceScopeFilter
      label="Scope"
      availableNamespaces={scopePickerNamespaces}
      selected={selectedScopes}
      onChange={handleScopeChange}
      minSelection={1}
    />
  );

  // --- Project filter popover ---
  const projectTrailing = showProjectFilter ? (
    <>
      <Button
        className={classes.triggerButton}
        endIcon={<ArrowDropDownIcon />}
        onClick={e => setProjectAnchor(e.currentTarget)}
      >
        {projectButtonLabel}
      </Button>
      <Popover
        open={Boolean(projectAnchor)}
        anchorEl={projectAnchor}
        onClose={() => setProjectAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.popoverPaper }}
      >
        <MenuList dense>
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              if (selectedProjects.length === projects.length) {
                // Deselect all
                setParams({
                  excludedProjects: projects.map(p => projectKey(p)).join(','),
                });
              } else {
                // Select all
                setParams({ excludedProjects: undefined });
              }
              setProjectAnchor(null);
            }}
          >
            <ListItemIcon>
              <Checkbox
                className={classes.checkbox}
                checked={selectedProjects.length === projects.length}
                indeterminate={
                  selectedProjects.length > 0 &&
                  selectedProjects.length < projects.length
                }
                color="primary"
                size="small"
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary="All" />
          </MenuItem>
          <Divider />
          {projects.map(p => {
            const key = projectKey(p);
            const isSelected = !excludedSet.has(key);
            return (
              <MenuItem
                key={key}
                className={classes.menuItem}
                onClick={() => handleProjectToggle(p)}
              >
                <ListItemIcon>
                  <Checkbox
                    className={classes.checkbox}
                    checked={isSelected}
                    color="primary"
                    size="small"
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={projectDisplayName(p)} />
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
    </>
  ) : undefined;

  // Only pass projects filter when not all are selected
  const activeProjectFilter = useMemo(
    () =>
      selectedProjects.length < projects.length ? selectedProjects : undefined,
    [selectedProjects, projects.length],
  );

  return (
    <Page themeId="tool">
      <Header title="Platform Overview" subtitle={displayView.description} />
      <Content stretch noPadding className={classes.content}>
        <GraphKindFilter
          selectedKinds={selectedKinds}
          onKindsChange={handleKindsChange}
          clusterScopeActive={clusterSelected}
          leading={scopeLeading}
          trailing={projectTrailing}
        />
        <PlatformOverviewGraphView
          view={effectiveView}
          namespaces={selectedScopes}
          projects={showProjectFilter ? activeProjectFilter : undefined}
          allProjects={showProjectFilter ? projects : undefined}
          onNodeClick={handleNodeClick}
        />
      </Content>
    </Page>
  );
}
